import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Download,
  Eye,
  Hourglass,
  MoreHorizontal,
  ReceiptText,
  RefreshCw,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AdminPage,
  ConfirmActionDialog,
  EmptyState,
  KpiCard,
  PillTabs,
  SearchField,
  StatusBadge,
  TablePagination,
  formatDate,
  formatNumber,
} from '../../../components/design-system/admin';
import type { StatusTone } from '../../../components/design-system/admin';
import { PdfViewerModal } from '../../../common/components/pdf-viewer-modal';
import { useWireAdminApi } from '../../../common/hooks/use-wire-admin-api.hook';
import { useVendorsApi } from '../../../common/hooks/use-vendors-api.hook';
import { InvoiceStatus, InvoiceType, VendorInvoice } from '../../../common/models/billing.model';
import { formatMoney, formatTotals, sumByCurrency } from '../../../modules/superadmin/billing/money';

type InvoiceFilter = InvoiceStatus | 'overdue' | 'all';

const STATUS_TONE: Record<InvoiceStatus, StatusTone> = {
  pending: 'warning',
  paid: 'success',
  void: 'neutral',
  expired: 'neutral',
};
const STATUS_KEY: Record<InvoiceStatus, string> = {
  pending: 'ADMIN_BILLING.INVOICES.STATUS.PENDING',
  paid: 'ADMIN_BILLING.INVOICES.STATUS.PAID',
  void: 'ADMIN_BILLING.INVOICES.STATUS.VOID',
  expired: 'ADMIN_BILLING.INVOICES.STATUS.EXPIRED',
};
const TYPE_KEY: Record<InvoiceType, string> = {
  initial: 'ADMIN_BILLING.INVOICES.TYPE.INITIAL',
  renewal: 'ADMIN_BILLING.INVOICES.TYPE.RENEWAL',
  change: 'ADMIN_BILLING.INVOICES.TYPE.CHANGE',
  fee: 'ADMIN_BILLING.INVOICES.TYPE.FEE',
  historical: 'ADMIN_BILLING.INVOICES.TYPE.HISTORICAL',
};

const PAGE_SIZE = 25;

const isOverdue = (inv: VendorInvoice): boolean => inv.status === 'pending' && !!inv.dueDate && new Date(inv.dueDate).getTime() < Date.now();
/** The invoice's vendor id — a plain id from the API, or a populated vendor. */
const vendorIdOf = (inv: VendorInvoice): string => String((inv.vendor as any)?._id ?? inv.vendor ?? '');
/** An empty total reads in `fallback`, the currency invoices are usually in — not a stray "$0". */
const totalOf = (list: VendorInvoice[], fallback?: string) =>
  formatTotals(sumByCurrency(list, (i) => i.amount, (i) => i.currency), fallback);

/** The currency most invoices are in. */
const mainCurrency = (list: VendorInvoice[]): string | undefined => {
  const byCurrency = sumByCurrency(list, () => 1, (i) => i.currency);
  return Array.from(byCurrency.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
};

interface VendorName {
  name: string;
  alias?: string;
}

/**
 * Super-admin queue of wire invoices. Confirm a wire payment (mark-paid activates
 * / advances the plan) or void an incorrect invoice; "Run maintenance" fires the
 * wire cron on demand. All invoices are loaded once and filtered client-side so
 * the headline numbers and status tabs stay in sync.
 */
export const WireInvoicesPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useWireAdminApi();
  const vendorsApi = useVendorsApi();

  const [filter, setFilter] = useState<InvoiceFilter>('pending');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [all, setAll] = useState<VendorInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState<Map<string, VendorName> | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [payDialog, setPayDialog] = useState<{ invoice: VendorInvoice; reference: string } | null>(null);
  const [voidTarget, setVoidTarget] = useState<VendorInvoice | null>(null);
  const [viewer, setViewer] = useState<{ inv: VendorInvoice; url: string | null } | null>(null);

  const openViewer = async (inv: VendorInvoice) => {
    setViewer({ inv, url: null });
    try {
      const url = await api.viewInvoicePdf(inv._id);
      setViewer((v) => (v && v.inv._id === inv._id ? { inv, url } : v));
    } catch {
      setViewer(null);
      toast.error(t('ADMIN_BILLING.INVOICES.PDF_OPEN_FAILED'));
    }
  };
  const closeViewer = () => setViewer((v) => { if (v?.url) window.URL.revokeObjectURL(v.url); return null; });
  const downloadPdf = (inv: VendorInvoice) =>
    api.downloadInvoicePdf(inv._id, inv.invoiceNumber).catch(() => toast.error(t('ADMIN_BILLING.INVOICES.PDF_DOWNLOAD_FAILED')));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAll((await api.listInvoices()) || []);
    } catch {
      toast.error(t('ADMIN_BILLING.INVOICES.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Whose invoice is it: the vendor names, loaded once and matched by id.
  useEffect(() => {
    vendorsApi.findAllVendors(1, 1000)
      .then((res: any) => {
        const map = new Map<string, VendorName>();
        for (const v of res?.data || []) map.set(String(v._id), { name: v.name, alias: v.alias });
        setVendors(map);
      })
      .catch(() => setVendors(new Map()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vendorOf = useCallback((inv: VendorInvoice) => vendors?.get(vendorIdOf(inv)) || null, [vendors]);

  const stats = useMemo(() => {
    const now = new Date();
    const pending = all.filter((i) => i.status === 'pending');
    const paid = all.filter((i) => i.status === 'paid');
    const paidThisMonth = paid.filter((i) => {
      if (!i.paidAt) return false;
      const d = new Date(i.paidAt);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    return {
      pending,
      overdue: all.filter(isOverdue),
      paid,
      paidThisMonth,
      billed: all.filter((i) => i.status === 'pending' || i.status === 'paid'),
      currency: mainCurrency(all),
      void: all.filter((i) => i.status === 'void').length,
      expired: all.filter((i) => i.status === 'expired').length,
    };
  }, [all]);

  const filtered = useMemo(() => {
    const byStatus = filter === 'all' ? all : filter === 'overdue' ? all.filter(isOverdue) : all.filter((i) => i.status === filter);
    const q = query.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter((i) =>
      [i.invoiceNumber, i.planName, vendorOf(i)?.name, vendorOf(i)?.alias].some((s) => (s || '').toLowerCase().includes(q)),
    );
  }, [all, filter, query, vendorOf]);

  useEffect(() => { setPage(0); }, [filter, query]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const confirmPay = async () => {
    if (!payDialog) return;
    setBusyId(payDialog.invoice._id);
    try {
      await api.markPaid(payDialog.invoice._id, payDialog.reference.trim() || undefined);
      toast.success(t(payDialog.invoice.type === 'fee' ? 'ADMIN_BILLING.INVOICES.PAY.DONE_FEE' : 'ADMIN_BILLING.INVOICES.PAY.DONE'));
      setPayDialog(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('ADMIN_BILLING.INVOICES.PAY.FAILED'));
    } finally {
      setBusyId(null);
    }
  };

  const doVoid = async (inv: VendorInvoice) => {
    setBusyId(inv._id);
    try {
      await api.voidInvoice(inv._id);
      toast.success(t('ADMIN_BILLING.INVOICES.VOIDING.DONE'));
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('ADMIN_BILLING.INVOICES.VOIDING.FAILED'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmVoid = async () => {
    if (!voidTarget) return;
    await doVoid(voidTarget);
    setVoidTarget(null);
  };

  const runCron = async () => {
    setRunning(true);
    try {
      const res = await api.runCron();
      toast.success(t('ADMIN_BILLING.INVOICES.MAINTENANCE_DONE', {
        expired: res.expired,
        reminded: res.reminded,
        renewals: res.renewalsIssued,
        disabled: res.disabled,
      }));
      await load();
    } catch {
      toast.error(t('ADMIN_BILLING.INVOICES.MAINTENANCE_FAILED'));
    } finally {
      setRunning(false);
    }
  };

  const firstLoad = loading && all.length === 0;
  const payInvoice = payDialog?.invoice;
  const payVendor = payInvoice ? vendorOf(payInvoice)?.name || t('ADMIN_BILLING.INVOICES.UNKNOWN_VENDOR') : '';
  const payKey = payInvoice?.type === 'fee'
    ? 'ADMIN_BILLING.INVOICES.PAY.DESCRIPTION_FEE'
    : payInvoice?.type === 'renewal'
      ? 'ADMIN_BILLING.INVOICES.PAY.DESCRIPTION_RENEWAL'
      : 'ADMIN_BILLING.INVOICES.PAY.DESCRIPTION';

  return (
    <AdminPage
      title={t('ADMIN_SHELL.NAV.INVOICES')}
      parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_BILLING') }]}
      description={t('ADMIN_BILLING.INVOICES.DESCRIPTION')}
      actions={
        <Button variant="outline" onClick={runCron} disabled={running}>
          <RefreshCw className={cn('size-4', running && 'animate-spin')} />
          {t('ADMIN_BILLING.INVOICES.RUN_MAINTENANCE')}
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="invoice-kpis">
        <KpiCard
          label={t('ADMIN_BILLING.INVOICES.KPI.PENDING')}
          value={formatNumber(stats.pending.length)}
          hint={t('ADMIN_BILLING.INVOICES.KPI.PENDING_HINT', { amount: totalOf(stats.pending, stats.currency) })}
          icon={<Hourglass />}
          loading={firstLoad}
          testId="kpi-invoices-pending"
        />
        <KpiCard
          label={t('ADMIN_BILLING.INVOICES.KPI.OVERDUE')}
          value={<span className={cn(stats.overdue.length > 0 && 'text-destructive')}>{formatNumber(stats.overdue.length)}</span>}
          hint={t('ADMIN_BILLING.INVOICES.KPI.OVERDUE_HINT', { amount: totalOf(stats.overdue, stats.currency) })}
          icon={<AlertCircle />}
          loading={firstLoad}
          testId="kpi-invoices-overdue"
        />
        <KpiCard
          label={t('ADMIN_BILLING.INVOICES.KPI.PAID_MONTH')}
          value={totalOf(stats.paidThisMonth, stats.currency)}
          hint={t('ADMIN_BILLING.INVOICES.KPI.PAID_MONTH_HINT', { amount: totalOf(stats.paid, stats.currency) })}
          icon={<CheckCircle2 />}
          loading={firstLoad}
          testId="kpi-invoices-paid"
        />
        <KpiCard
          label={t('ADMIN_BILLING.INVOICES.KPI.TOTAL')}
          value={formatNumber(all.length)}
          hint={t('ADMIN_BILLING.INVOICES.KPI.TOTAL_HINT', { amount: totalOf(stats.billed, stats.currency) })}
          icon={<ReceiptText />}
          loading={firstLoad}
          testId="kpi-invoices-total"
        />
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <PillTabs<InvoiceFilter>
          value={filter}
          onChange={setFilter}
          ariaLabel={t('ADMIN_BILLING.INVOICES.TABS_LABEL')}
          options={[
            { value: 'pending', label: t('ADMIN_BILLING.INVOICES.STATUS.PENDING'), count: stats.pending.length },
            { value: 'overdue', label: t('ADMIN_BILLING.INVOICES.STATUS.OVERDUE'), count: stats.overdue.length },
            { value: 'paid', label: t('ADMIN_BILLING.INVOICES.STATUS.PAID'), count: stats.paid.length },
            { value: 'void', label: t('ADMIN_BILLING.INVOICES.STATUS.VOID'), count: stats.void },
            { value: 'expired', label: t('ADMIN_BILLING.INVOICES.STATUS.EXPIRED'), count: stats.expired },
            { value: 'all', label: t('ADMIN_SHELL.COMMON.ALL'), count: all.length },
          ]}
        />
        <SearchField value={query} onChange={setQuery} placeholder={t('ADMIN_BILLING.INVOICES.SEARCH')} />
      </div>

      <Card className="gap-0 py-0">
        {firstLoad ? (
          <div className="flex flex-col gap-3 p-4">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<ReceiptText />}
            title={query.trim() ? t('ADMIN_BILLING.INVOICES.EMPTY_SEARCH') : t('ADMIN_BILLING.INVOICES.EMPTY')}
            description={query.trim() ? undefined : t('ADMIN_BILLING.INVOICES.EMPTY_HINT')}
            action={query.trim() ? (
              <Button variant="outline" onClick={() => setQuery('')}>{t('ADMIN_SHELL.COMMON.CLEAR')}</Button>
            ) : undefined}
            className="py-16"
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="ps-4">{t('ADMIN_BILLING.INVOICES.COL.INVOICE')}</TableHead>
                  <TableHead>{t('ADMIN_BILLING.INVOICES.COL.VENDOR')}</TableHead>
                  <TableHead>{t('ADMIN_BILLING.INVOICES.COL.PLAN')}</TableHead>
                  <TableHead>{t('ADMIN_BILLING.INVOICES.COL.DUE')}</TableHead>
                  <TableHead className="text-end">{t('ADMIN_BILLING.INVOICES.COL.AMOUNT')}</TableHead>
                  <TableHead>{t('ADMIN_BILLING.INVOICES.COL.STATUS')}</TableHead>
                  <TableHead className="pe-4 text-end"><span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((inv) => {
                  const overdue = isOverdue(inv);
                  const pending = inv.status === 'pending';
                  const busy = busyId === inv._id;
                  const vendor = vendorOf(inv);
                  const vendorId = vendorIdOf(inv);
                  const paidMeta = [
                    inv.paidAt ? formatDate(inv.paidAt) : '',
                    inv.confirmedBy ? t('ADMIN_BILLING.INVOICES.CONFIRMED_BY', { name: inv.confirmedBy }) : '',
                  ].filter(Boolean).join(' · ');
                  return (
                    <TableRow key={inv._id} data-testid={`invoice-row-${inv._id}`}>
                      <TableCell className="ps-4">
                        <button
                          type="button"
                          className="font-medium tabular-nums hover:underline"
                          onClick={() => openViewer(inv)}
                        >
                          {inv.invoiceNumber}
                        </button>
                        {inv.issuedAt && (
                          <div className="text-xs text-muted-foreground tabular-nums">{formatDate(inv.issuedAt)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {vendor ? (
                          <Link to={`/vendors/${vendorId}/edit`} className="block min-w-0 hover:underline">
                            <span className="font-medium">{vendor.name}</span>
                            {vendor.alias && <span className="ms-1.5 text-xs text-muted-foreground">@{vendor.alias}</span>}
                          </Link>
                        ) : vendors ? (
                          <span className="text-muted-foreground">{t('ADMIN_BILLING.INVOICES.UNKNOWN_VENDOR')}</span>
                        ) : (
                          <Skeleton className="h-4 w-24" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div>{inv.planName || '—'}</div>
                        <div className="text-xs text-muted-foreground">
                          {t(TYPE_KEY[inv.type] || TYPE_KEY.initial)} · {t(inv.interval === 'year' ? 'ADMIN_BILLING.INVOICES.INTERVAL.YEAR' : 'ADMIN_BILLING.INVOICES.INTERVAL.MONTH')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="tabular-nums">{formatDate(inv.dueDate)}</div>
                        {overdue && (
                          <StatusBadge tone="danger" className="mt-0.5">{t('ADMIN_BILLING.INVOICES.STATUS.OVERDUE')}</StatusBadge>
                        )}
                      </TableCell>
                      <TableCell className="text-end font-medium tabular-nums">{formatMoney(inv.amount, inv.currency)}</TableCell>
                      <TableCell>
                        <StatusBadge tone={STATUS_TONE[inv.status] || 'neutral'}>{t(STATUS_KEY[inv.status] || STATUS_KEY.pending)}</StatusBadge>
                        {inv.status === 'paid' && paidMeta && (
                          <div className="mt-0.5 text-xs text-muted-foreground">{paidMeta}</div>
                        )}
                      </TableCell>
                      <TableCell className="pe-4">
                        <div className="flex items-center justify-end gap-1">
                          {pending && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy}
                              onClick={() => setPayDialog({ invoice: inv, reference: '' })}
                            >
                              {t('ADMIN_BILLING.INVOICES.MARK_PAID')}
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('ADMIN_BILLING.INVOICES.ROW_ACTIONS', { number: inv.invoiceNumber })}
                              >
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-44">
                              <DropdownMenuItem onClick={() => openViewer(inv)}>
                                <Eye className="size-4" />
                                {t('ADMIN_BILLING.INVOICES.VIEW_PDF')}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => downloadPdf(inv)}>
                                <Download className="size-4" />
                                {t('ADMIN_BILLING.INVOICES.DOWNLOAD_PDF')}
                              </DropdownMenuItem>
                              {pending && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem variant="destructive" disabled={busy} onClick={() => setVoidTarget(inv)}>
                                    <Ban className="size-4" />
                                    {t('ADMIN_BILLING.INVOICES.VOID')}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {filtered.length > PAGE_SIZE && (
              <TablePagination
                className="border-t"
                page={safePage}
                pageSize={PAGE_SIZE}
                total={filtered.length}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {/* Mark paid: the bank reference is optional */}
      <ConfirmActionDialog
        open={!!payDialog}
        onOpenChange={(open) => !open && setPayDialog(null)}
        title={t('ADMIN_BILLING.INVOICES.PAY.TITLE')}
        description={payInvoice ? t(payKey, {
          number: payInvoice.invoiceNumber,
          amount: formatMoney(payInvoice.amount, payInvoice.currency),
          vendor: payVendor,
          plan: payInvoice.planName,
        }) : undefined}
        confirmLabel={t('ADMIN_BILLING.INVOICES.PAY.CONFIRM')}
        busy={busyId !== null}
        onConfirm={confirmPay}
      >
        <div className="grid gap-2">
          <Label htmlFor="invoice-pay-reference">{t('ADMIN_BILLING.INVOICES.PAY.REFERENCE')}</Label>
          <Input
            id="invoice-pay-reference"
            placeholder={t('ADMIN_BILLING.INVOICES.PAY.REFERENCE_PLACEHOLDER')}
            value={payDialog?.reference || ''}
            onChange={(e) => setPayDialog((p) => (p ? { ...p, reference: e.target.value } : p))}
          />
        </div>
      </ConfirmActionDialog>

      <ConfirmActionDialog
        open={!!voidTarget}
        onOpenChange={(open) => !open && setVoidTarget(null)}
        title={t('ADMIN_BILLING.INVOICES.VOIDING.TITLE', { number: voidTarget?.invoiceNumber })}
        description={t('ADMIN_BILLING.INVOICES.VOIDING.DESCRIPTION')}
        confirmLabel={t('ADMIN_BILLING.INVOICES.VOIDING.CONFIRM')}
        destructive
        busy={!!voidTarget && busyId === voidTarget._id}
        onConfirm={confirmVoid}
      />

      <PdfViewerModal
        url={viewer?.url ?? null}
        loading={!!viewer && !viewer.url}
        title={viewer ? t('ADMIN_BILLING.INVOICES.VIEWER_TITLE', { number: viewer.inv.invoiceNumber }) : t('ADMIN_BILLING.INVOICES.VIEWER_TITLE_EMPTY')}
        onDownload={viewer ? () => downloadPdf(viewer.inv) : undefined}
        onClose={closeViewer}
      />
    </AdminPage>
  );
};

export default WireInvoicesPage;
